import React from 'react';
import Icon from '../../../components/AppIcon';

const VerificationCenter = () => {
  const verificationSteps = [
    {
      step: 1,
      title: "Vérification Documentaire",
      description: "Authentification des titres de propriété et documents officiels",
      icon: "FileSearch",
      duration: "24-48h",
      status: "automated"
    },
    {
      step: 2,
      title: "Contrôle Cadastral",
      description: "Vérification des limites et de la conformité cadastrale",
      icon: "Map",
      duration: "2-3 jours",
      status: "manual"
    },
    {
      step: 3,
      title: "Enquête de Voisinage",
      description: "Confirmation auprès des autorités locales et voisins",
      icon: "Users",
      duration: "3-5 jours",
      status: "field"
    },
    {
      step: 4,
      title: "Validation Juridique",
      description: "Examen par nos experts juridiques partenaires",
      icon: "Scale",
      duration: "1-2 jours",
      status: "expert"
    },
    {
      step: 5,
      title: "Rapport Final",
      description: "Certification complète avec recommandations",
      icon: "CheckCircle",
      duration: "1 jour",
      status: "complete"
    }
  ];

  const securityFeatures = [
    {
      title: "Blockchain Immobilier",
      description: "Enregistrement sécurisé des transactions sur blockchain privée",
      icon: "Shield",
      benefit: "Traçabilité complète"
    },
    {
      title: "Partenariat Notarial",
      description: "Réseau de notaires certifiés et vérifiés",
      icon: "Award",
      benefit: "Expertise garantie"
    },
    {
      title: "Assurance Transaction",
      description: "Couverture d'assurance pour chaque transaction vérifiée",
      icon: "Umbrella",
      benefit: "Protection financière"
    },
    {
      title: "Suivi Temps Réel",
      description: "Monitoring continu du statut de vérification",
      icon: "Activity",
      benefit: "Transparence totale"
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      automated: 'text-blue-400 bg-blue-500/10',
      manual: 'text-orange-400 bg-orange-500/10',
      field: 'text-purple-400 bg-purple-500/10',
      expert: 'text-emerald-400 bg-emerald-500/10',
      complete: 'text-brand-gold bg-brand-gold/10'
    };
    return colors?.[status] || 'text-white/60 bg-white/5';
  };

  return (
    <section className="py-24 bg-transparent border-none outline-none relative z-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        
        {/* En-tête */}
        <div className="text-center mb-20">
          <p className="text-xs font-bold text-brand-gold uppercase tracking-widest mb-3">
            Anti-Fraude
          </p>
          <h2 className="text-4xl lg:text-5xl font-heading font-bold text-white mb-6 tracking-tight">
            Centre de Vérification
          </h2>
          <p className="text-lg text-white/50 max-w-3xl mx-auto font-light leading-relaxed">
            Notre processus de vérification stricte en 5 étapes garantit la sécurité totale de votre investissement immobilier.
          </p>
        </div>

        {/* Processus de Vérification */}
        <div className="mb-24">
          <h3 className="text-2xl font-heading font-bold text-white mb-12 text-center tracking-tight">
            Processus de Vérification
          </h3>
          
          <div className="relative">
            {/* Progress Line (Glowing) */}
            <div className="absolute top-16 left-0 right-0 h-px bg-white/10 hidden lg:block">
              <div className="h-full bg-gradient-to-r from-brand-gold/20 via-brand-gold to-brand-gold/20 w-full" />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 relative z-10">
              {verificationSteps?.map((step) => (
                <div key={step?.step} className="bg-[#111111] rounded-3xl p-6 shadow-2xl border-none text-center group hover:-translate-y-2 transition-transform duration-500">
                  <div className="w-16 h-16 bg-[#1A1A1A] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner border border-white/5 group-hover:border-brand-gold/30 transition-colors">
                    <Icon name={step?.icon} size={24} className="text-brand-gold" />
                  </div>
                  
                  <div className={`inline-block px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4 ${getStatusColor(step?.status)}`}>
                    Étape {step?.step}
                  </div>
                  
                  <h4 className="text-base font-heading font-bold text-white mb-3 leading-snug">
                    {step?.title}
                  </h4>
                  
                  <p className="text-sm text-white/40 font-light mb-6 leading-relaxed">
                    {step?.description}
                  </p>
                  
                  <div className="flex items-center justify-center gap-2 text-xs font-medium text-white/60 bg-white/5 py-2 rounded-lg">
                    <Icon name="Clock" size={14} className="text-white/30" />
                    <span>{step?.duration}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Garanties de Sécurité */}
        <div className="bg-[#111111] rounded-3xl shadow-2xl border-none outline-none p-8 md:p-12 mb-12">
          <h3 className="text-2xl font-heading font-bold text-white mb-10 text-center tracking-tight">
            Garanties de Sécurité
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securityFeatures?.map((feature, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner group-hover:scale-110 transition-transform">
                  <Icon name={feature?.icon} size={24} className="text-brand-gold" />
                </div>
                
                <h4 className="text-lg font-heading font-bold text-white mb-3">
                  {feature?.title}
                </h4>
                
                <p className="text-sm text-white/40 font-light mb-6 leading-relaxed">
                  {feature?.description}
                </p>
                
                <div className="inline-block px-4 py-1.5 bg-brand-gold/10 text-brand-gold rounded-full text-[10px] font-bold uppercase tracking-widest">
                  {feature?.benefit}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Indicators (KPIs) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[#111111] rounded-3xl p-8 shadow-2xl border-none text-center">
            <div className="text-4xl md:text-5xl font-heading font-bold text-white mb-2 tracking-tight">
              2 847
            </div>
            <p className="text-xs font-bold text-white/30 uppercase tracking-widest">Propriétés Vérifiées</p>
          </div>
          
          <div className="bg-[#111111] rounded-3xl p-8 shadow-2xl border-none text-center">
            <div className="text-4xl md:text-5xl font-heading font-bold text-emerald-400 mb-2 tracking-tight">
              99.2<span className="text-3xl">%</span>
            </div>
            <p className="text-xs font-bold text-white/30 uppercase tracking-widest">Taux de Précision</p>
          </div>
          
          <div className="bg-[#111111] rounded-3xl p-8 shadow-2xl border-none text-center">
            <div className="text-4xl md:text-5xl font-heading font-bold text-brand-gold mb-2 tracking-tight">
              24<span className="text-3xl">h</span>
            </div>
            <p className="text-xs font-bold text-white/30 uppercase tracking-widest">Délai Moyen</p>
          </div>
        </div>

      </div>
    </section>
  );
};

export default VerificationCenter;